import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import ModalContext from 'contexts/ModalContext'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { CHARTER } from 'constants/provider'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { refreshMultiCartFlightItem } from 'components/Pages/UserCartPage/SharedCarts/hooks/refreshMultiCartFlightItem'
import {
  isFlightCartErrorResponse,
  mapCheckoutFlightItemToTripChatFlightCartPayload,
  upsertTripChatFlightCartItem,
  type FlightCartRequest,
} from 'tripPlanner/api/flightCart'
import { isLeFlightSavedItemData, type LeFlightSavedItemData } from 'tripPlanner/api/types'
import { isResolvedFlightJourneyId } from 'tripPlanner/components/Chat/Flights/flightJourneyId'
import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, FlightsFareTypes } from 'constants/flight'
import { searchFlights } from 'api/flights'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import FlightSearchDrawer, {
  type FlightSearchDrawerCartSelection,
} from '../components/FlightSearchDrawer/FlightSearchDrawer'
import {
  findTransportConnectionForOption,
  getFlightSearchDrawerParamsFromTransport,
} from '../components/FlightSearchDrawer/flightSearchDrawerParams'
import { prepareSelectedFlightCheckoutItem } from '../components/ShortlistedFlightsSection/prepareFlightCheckoutItem'
import { buildJourneyFromSavedFlight } from '../components/ShortlistedFlightsSection/buildSavedFlightJourney'
import { parseSearchParams } from '../components/ShortlistedFlightsSection/flightShortlistUtils'
import { cacheShortlistedJourneys, getCachedJourney } from '../components/ShortlistedFlightsSection/shortlistedJourneyCache'
import { findShortlistedFlightJourneys } from '../components/ShortlistedFlightsSection/shortlistedFlightJourneyMatching'
import { useItineraryContext } from '../contexts/ItineraryContext'
import {
  buildFlightCartLegMetadata,
  isFlightSearchExpired,
  resolveFlightCartIntent,
  resolveFlightCartReactivationMode,
} from '../utils/flightCartPanelUtils'
import { getCartPanelFlightRefreshKey, type CartPanelFlightItem } from './useCartPanelItems'
import { tripPlannerCartItemsKey } from './useTripPlannerCartItems'

type FlightCartActionMode = 'choose' | 'refresh' | 'change'

interface UseTripChatFlightCartActionsArgs {
  chatId: number | undefined
  multiCartId: string | undefined
  isStreaming: boolean
  isLoggedIn: boolean
  setRefreshFailedFlightKeys: Dispatch<SetStateAction<Record<string, boolean>>>
}

interface UseTripChatFlightCartActionsReturn {
  flightSearchDrawer: ReactNode
  handleChooseFlight: (item: CartPanelFlightItem) => Promise<boolean>
  handleRefreshFlight: (item: CartPanelFlightItem) => Promise<boolean>
  handleChangeFlight: (item: CartPanelFlightItem) => void
  refreshingFlightKey: string | undefined
  isFlightCartMutationPending: boolean
}

interface FlightCartDrawerState {
  item: CartPanelFlightItem
  mode: FlightCartActionMode
}

type PersistFlightCartSelectionResult = 'success' | 'cancelled' | 'failed'

function hasSelectedJourneyChanged(
  item: CartPanelFlightItem,
  selection: FlightSearchDrawerCartSelection,
): boolean {
  if (!isLeFlightSavedItemData(item.savedItemData)) return true
  const outboundChanged = item.savedItemData.journeyKey !== selection.departingJourney.bookingInfo.journeyKey
  const returnChanged = item.returnSavedItemData && selection.returningJourney
    ? item.returnSavedItemData.journeyKey !== selection.returningJourney.bookingInfo.journeyKey
    : false
  return outboundChanged || returnChanged
}

function shouldSendLegMetadata(intent: FlightCartRequest['intent']): boolean {
  return intent === 'convert_and_sync' || intent === 'replace'
}

function getDrawerFareType(item: CartPanelFlightItem): string | undefined {
  if (item.returnOption) return 'return'
  if (!isLeFlightSavedItemData(item.savedItemData)) return undefined
  return parseSearchParams(item.savedItemData.searchParams)?.fareType
}

interface FoundJourneys {
  departingJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  searchId: string | undefined
  sessionId: string | undefined
  searchExpiry: number
}

async function searchAndFindJourneys({
  searchParams,
  departingJourneyKey,
  returningJourneyKey,
  currency,
  region,
  email,
  enableSabreContent,
  requestLuxPlusPricing,
}: {
  searchParams: FlightSearchParams
  departingJourneyKey: string
  returningJourneyKey?: string
  currency: string
  region: string
  email: string | undefined
  enableSabreContent: boolean
  requestLuxPlusPricing: boolean
}): Promise<FoundJourneys | undefined> {
  try {
    const result = await searchFlights(
      {
        itinerary: searchParams.itinerary,
        currency,
        occupancies: searchParams.occupancies,
        fareClass: searchParams.fareClass,
        fareType: searchParams.fareType,
        region,
        email,
      },
      {
        enableSabreContent,
        ...(requestLuxPlusPricing ? { requestLuxPlusPricing: true } : {}),
      },
    )
    // Cache every journey from this search (all multi-city segments at once), so
    // the other legs of the same group resolve from cache instead of each firing
    // its own full search — that duplicate searching is what made the cart slow.
    cacheShortlistedJourneys(result.onewayFares.flat(), result.searchId, result.sessionId, requestLuxPlusPricing)
    const { departingJourney, returningJourney } = findShortlistedFlightJourneys({
      onewayFares: result.onewayFares,
      departingJourneyKey,
      returningJourneyKey,
    })
    if (!departingJourney) return undefined
    if (returningJourneyKey) {
      if (!returningJourney) return undefined
      return {
        departingJourney,
        returningJourney,
        searchId: result.searchId,
        sessionId: result.sessionId,
        searchExpiry: Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000,
      }
    }
    return {
      departingJourney,
      searchId: result.searchId,
      sessionId: result.sessionId,
      searchExpiry: Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000,
    }
  } catch {
    return undefined
  }
}

function setFlightRefreshFailed(
  setRefreshFailedFlightKeys: Dispatch<SetStateAction<Record<string, boolean>>>,
  item: CartPanelFlightItem,
  hasFailed: boolean,
) {
  const refreshKey = getCartPanelFlightRefreshKey(item)
  setRefreshFailedFlightKeys(prev => {
    if (hasFailed) {
      if (prev[refreshKey]) return prev
      return {
        ...prev,
        [refreshKey]: true,
      }
    }

    if (!prev[refreshKey]) return prev
    const next = { ...prev }
    delete next[refreshKey]
    return next
  })
}

function getSavedFlightDepartureDate(savedItemData: LeFlightSavedItemData): string | undefined {
  return typeof savedItemData.departureTime === 'string' ? savedItemData.departureTime.slice(0, 10) : undefined
}

function doesItineraryLegMatchSavedFlight(
  savedItemData: LeFlightSavedItemData,
  leg: FlightSearchParams['itinerary'][number] | undefined,
): boolean {
  if (!leg) return false
  if (leg.departureAirport !== savedItemData.departureAirport) return false
  if (leg.arrivalAirport !== savedItemData.arrivalAirport) return false
  const departureDate = getSavedFlightDepartureDate(savedItemData)
  return !departureDate || leg.departureDate === departureDate
}

function getMultiCityItineraryIndex(
  savedItemData: LeFlightSavedItemData,
  itinerary: FlightSearchParams['itinerary'],
): number | undefined {
  if (savedItemData.legOrder === undefined) return undefined

  const matchingIndex = itinerary.findIndex(leg => doesItineraryLegMatchSavedFlight(savedItemData, leg))
  if (matchingIndex >= 0) return matchingIndex

  if (itinerary[savedItemData.legOrder]) return savedItemData.legOrder

  const oneBasedIndex = savedItemData.legOrder - 1
  return itinerary[oneBasedIndex] ? oneBasedIndex : undefined
}

function doesSelectionMatchItineraryRouteAndDate(
  item: CartPanelFlightItem,
  selection: FlightSearchDrawerCartSelection,
): boolean {
  if (!isLeFlightSavedItemData(item.savedItemData)) return true
  const expectedItinerary = parseSearchParams(item.savedItemData.searchParams)?.itinerary
  if (!expectedItinerary) return true

  const selectedItinerary = selection.searchParams.itinerary
  const isMultiCity = item.savedItemData.flightGroupType === 'multi_city'
  // Fail closed on a multi-city leg with no legOrder rather than defaulting to 0
  // (which would validate every such leg against itinerary leg 0 and let the
  // wrong leg persist). Mirrors the grouped drawer-replacement fail-closed path,
  // so all multi-city persistence routes (choose / refresh / auto-convert) agree.
  const startIndex = isMultiCity ? getMultiCityItineraryIndex(item.savedItemData, expectedItinerary) : 0
  if (startIndex === undefined) return false
  const expectedLegCount = isMultiCity ? 1 : item.returnOption ? 2 : 1
  for (let offset = 0; offset < expectedLegCount; offset += 1) {
    const index = startIndex + offset
    const expectedLeg = expectedItinerary[index]
    const selectedLeg = selectedItinerary[index]
    if (!expectedLeg || !selectedLeg) return false
    if (expectedLeg.departureAirport !== selectedLeg.departureAirport) return false
    if (expectedLeg.arrivalAirport !== selectedLeg.arrivalAirport) return false
    if (expectedLeg.departureDate !== selectedLeg.departureDate) return false
  }

  return true
}

export function useTripChatFlightCartActions({
  chatId,
  multiCartId,
  isStreaming,
  isLoggedIn,
  setRefreshFailedFlightKeys,
}: UseTripChatFlightCartActionsArgs): UseTripChatFlightCartActionsReturn {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const showModal = useContext(ModalContext)
  const currentCurrency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const enableSabreContent = useOptimizelyExperiment(OptimizelyFeatureFlags.flightsEnableSabreContent) ?? false
  const email = useAppSelector(state => state.auth.account.email)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const canRequestLuxPlusFlightPricing = canRedeemLuxPlusBenefits && currentRegionCode === 'AU'
  const {
    itineraryData,
    refetchCurrentChatItinerary,
    isFlightCartMutationPending,
    setFlightCartMutationPending,
  } = useItineraryContext()
  const [drawerState, setDrawerState] = useState<FlightCartDrawerState | undefined>(undefined)
  const [refreshingFlightKey, setRefreshingFlightKey] = useState<string | undefined>(undefined)
  const submittingRef = useRef(false)

  const defaultErrorMessage = intl.formatMessage({
    id: '8AeJDty85uPW5ZcHZ00i',
    defaultMessage: 'Unable to update this flight. Please try again.',
    description: 'Error snackbar shown when updating a TripChat flight cart item fails',
  })

  const assistantTurnErrorMessage = intl.formatMessage({
    id: 'Owjd3IjmCAM-Ngp_LF2I',
    defaultMessage: 'TripChat is still updating. Please try again in a moment.',
    description: 'Error snackbar shown when TripChat is mid-response while updating a flight cart item',
  })

  const routeDateMismatchMessage = intl.formatMessage({
    id: '9ykrfy0DKFfIRDKao7il',
    defaultMessage: 'Choose a flight with the same route and date to update this cart item.',
    description: 'Error snackbar shown when a TripChat cart flight selection changes route or date',
  })

  const closeDrawer = useCallback(() => {
    setDrawerState(undefined)
  }, [])

  const openDrawer = useCallback((item: CartPanelFlightItem, mode: FlightCartActionMode) => {
    if (isStreaming || isFlightCartMutationPending) return
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return
    }
    setDrawerState({ item, mode })
  }, [isStreaming, isFlightCartMutationPending, isLoggedIn, showModal])

  const flightUnavailableMessage = intl.formatMessage({
    id: 'q1yFA0lpAVDPYbrVxejE',
    defaultMessage: 'This flight is no longer available. Please search for a new flight.',
    description: 'Error shown when the saved flight can no longer be found in search results',
  })

  const invalidateTripPlannerCartItems = useCallback(async() => {
    if (!multiCartId) return
    await queryClient.invalidateQueries({ queryKey: tripPlannerCartItemsKey(multiCartId) })
  }, [multiCartId, queryClient])

  const persistFlightCartSelection = useCallback(async(
    item: CartPanelFlightItem,
    selection: FlightSearchDrawerCartSelection,
    mode: FlightCartActionMode,
  ): Promise<PersistFlightCartSelectionResult> => {
    let mutationPendingSet = false
    try {
      if (!chatId) return 'failed'

      if (!doesSelectionMatchItineraryRouteAndDate(item, selection)) {
        showSnackbar(routeDateMismatchMessage, 'critical')
        return 'failed'
      }

      const preparedResult = await prepareSelectedFlightCheckoutItem({
        departingJourney: selection.departingJourney,
        returningJourney: selection.returningJourney,
        searchParams: selection.searchParams,
        searchId: selection.searchId,
        sessionId: selection.sessionId,
        searchExpiry: selection.searchExpiry,
        showModal,
      })

      if (preparedResult.status === 'cancelled') return 'cancelled'
      if (preparedResult.status !== 'success') {
        showSnackbar(defaultErrorMessage, 'critical')
        return 'failed'
      }

      const selectedJourneyChanged = hasSelectedJourneyChanged(item, selection)
      const isUnsyncedRefresh = mode === 'refresh' && !item.flightCartItem
      const isUnsyncedChoose = mode === 'choose' && !item.flightCartItem
      const intent = resolveFlightCartIntent({
        savedItemData: item.savedItemData,
        hasActiveCartItem: !!item.flightCartItem,
        isRefresh: mode === 'refresh' && !!item.flightCartItem && !selectedJourneyChanged,
        selectedJourneyChanged: selectedJourneyChanged || isUnsyncedRefresh || isUnsyncedChoose,
      })
      const outboundMetadata = buildFlightCartLegMetadata({
        journey: selection.departingJourney,
        savedItemData: isLeFlightSavedItemData(item.savedItemData) ? item.savedItemData : undefined,
        searchId: preparedResult.prepared.searchId,
        searchExpiry: preparedResult.prepared.searchExpiry,
        searchParams: JSON.stringify(selection.searchParams),
      })
      const returnMetadata = selection.returningJourney ? buildFlightCartLegMetadata({
        journey: selection.returningJourney,
        savedItemData: item.returnSavedItemData,
        searchId: preparedResult.prepared.searchId,
        searchExpiry: preparedResult.prepared.searchExpiry,
        searchParams: JSON.stringify(selection.searchParams),
      }) : undefined

      if (shouldSendLegMetadata(intent) && !outboundMetadata) {
        showSnackbar(defaultErrorMessage, 'critical')
        return 'failed'
      }

      const requestBody: FlightCartRequest = {
        intent,
        reactivationMode: resolveFlightCartReactivationMode({
          cartExcluded: item.cartExcluded,
          isAvailabilityBlocked: item.isAvailabilityBlocked,
        }),
        flightPayload: preparedResult.prepared.cartPayload,
        returnItemId: item.returnTripItemId,
        legMetadata: shouldSendLegMetadata(intent) && outboundMetadata ? {
          outbound: outboundMetadata,
          return: returnMetadata,
        } : undefined,
      }

      setFlightCartMutationPending(true)
      mutationPendingSet = true
      await upsertTripChatFlightCartItem(chatId, item.tripItemId, requestBody)
      setFlightRefreshFailed(setRefreshFailedFlightKeys, item, false)
      refetchCurrentChatItinerary()
      await invalidateTripPlannerCartItems()
      return 'success'
    } catch(error) {
      const message = isFlightCartErrorResponse(error) && error.code === 'ASSISTANT_TURN_ACTIVE'
        ? assistantTurnErrorMessage
        : defaultErrorMessage
      showSnackbar(message, 'critical')
      refetchCurrentChatItinerary()
      await invalidateTripPlannerCartItems()
      return 'failed'
    } finally {
      if (mutationPendingSet) setFlightCartMutationPending(false)
    }
  }, [
    assistantTurnErrorMessage,
    chatId,
    defaultErrorMessage,
    invalidateTripPlannerCartItems,
    refetchCurrentChatItinerary,
    routeDateMismatchMessage,
    setFlightCartMutationPending,
    setRefreshFailedFlightKeys,
    showModal,
  ])

  const resolveSavedFlightSelection = useCallback(async(item: CartPanelFlightItem) => {
    if (!isLeFlightSavedItemData(item.savedItemData) || !item.savedItemData.journeyKey) return undefined
    const { journeyKey, searchParams: savedSearchParams } = item.savedItemData
    const returningKey = item.returnSavedItemData?.journeyKey
    const parsedSearchParams = parseSearchParams(savedSearchParams)

    if (item.savedItemData.flightGroupType === 'multi_city' && parsedSearchParams) {
      const saved = item.savedItemData

      // Fast path: while the saved fare is still valid and contains the live
      // journey id, convert straight from saved data with no live search. If
      // journeyId is missing, the saved leg is display-complete but not
      // checkout-complete, so resolve it before creating the cart row.
      if (
        saved.searchId &&
        isResolvedFlightJourneyId(saved.journeyId, saved.journeyKey) &&
        !isFlightSearchExpired(saved.searchExpiry)
      ) {
        const journey = buildJourneyFromSavedFlight(saved)
        if (journey) {
          return {
            departingJourney: journey,
            returningJourney: undefined,
            searchParams: parsedSearchParams,
            searchId: saved.searchId,
            sessionId: undefined,
            searchExpiry: saved.searchExpiry ??
              Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000,
          }
        }
      }

      const cached = getCachedJourney(journeyKey, canRequestLuxPlusFlightPricing)
      if (cached?.searchId) {
        return {
          departingJourney: cached.journey,
          returningJourney: undefined,
          searchParams: parsedSearchParams,
          searchId: cached.searchId,
          sessionId: cached.sessionId,
          searchExpiry: cached.cachedAt + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000,
        }
      }

      // Resolve missing/expired saved cart data with one full multi-city search.
      // searchAndFindJourneys caches every segment result, so the remaining
      // legs in this group reuse the same searchId instead of each creating
      // their own independent one-way session.
      {
        setFlightCartMutationPending(true)
        let found: FoundJourneys | undefined
        try {
          found = await searchAndFindJourneys({
            searchParams: parsedSearchParams,
            departingJourneyKey: journeyKey,
            currency: currentCurrency,
            region: currentRegionCode,
            email,
            enableSabreContent,
            requestLuxPlusPricing: canRequestLuxPlusFlightPricing,
          })
        } finally {
          setFlightCartMutationPending(false)
        }
        if (found?.searchId) {
          return {
            departingJourney: found.departingJourney,
            returningJourney: undefined,
            searchParams: parsedSearchParams,
            searchId: found.searchId,
            sessionId: found.sessionId,
            searchExpiry: found.searchExpiry,
          }
        }
      }
      return undefined
    }

    // Resolve the journeys: cache first, then live search as fallback
    let found: FoundJourneys | undefined

    const cachedDeparting = getCachedJourney(journeyKey, canRequestLuxPlusFlightPricing)
    const cachedReturning = returningKey ? getCachedJourney(returningKey, canRequestLuxPlusFlightPricing) : undefined

    if (cachedDeparting?.searchId && (!returningKey || cachedReturning)) {
      found = {
        departingJourney: cachedDeparting.journey,
        returningJourney: cachedReturning?.journey,
        searchId: cachedDeparting.searchId,
        sessionId: cachedDeparting.sessionId,
        searchExpiry: cachedDeparting.cachedAt + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000,
      }
    } else if (parsedSearchParams) {
      setFlightCartMutationPending(true)
      try {
        found = await searchAndFindJourneys({
          searchParams: parsedSearchParams,
          departingJourneyKey: journeyKey,
          returningJourneyKey: returningKey,
          currency: currentCurrency,
          region: currentRegionCode,
          email,
          enableSabreContent,
          requestLuxPlusPricing: canRequestLuxPlusFlightPricing,
        })
      } finally {
        setFlightCartMutationPending(false)
      }
      if (found) {
        const toCache = [found.departingJourney]
        if (found.returningJourney) toCache.push(found.returningJourney)
        cacheShortlistedJourneys(toCache, found.searchId, found.sessionId, canRequestLuxPlusFlightPricing)
      }
    }

    if (!found?.searchId || !parsedSearchParams) return undefined

    return {
      departingJourney: found.departingJourney,
      returningJourney: found.returningJourney,
      searchParams: parsedSearchParams,
      searchId: found.searchId,
      sessionId: found.sessionId,
      searchExpiry: found.searchExpiry,
    }
  }, [
    currentCurrency,
    currentRegionCode,
    canRequestLuxPlusFlightPricing,
    email,
    enableSabreContent,
    setFlightCartMutationPending,
  ])

  const handleChooseFlight = useCallback(async(item: CartPanelFlightItem): Promise<boolean> => {
    if (isStreaming || isFlightCartMutationPending || submittingRef.current) return false
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return false
    }
    if (!chatId) return false
    if (!isLeFlightSavedItemData(item.savedItemData) || !item.savedItemData.journeyKey) {
      openDrawer(item, 'choose')
      return false
    }

    const selection = await resolveSavedFlightSelection(item)
    if (!selection) {
      showSnackbar(flightUnavailableMessage, 'critical')
      return false
    }

    submittingRef.current = true
    setRefreshingFlightKey(item.key)
    try {
      const result = await persistFlightCartSelection(item, selection, 'choose')
      return result === 'success'
    } finally {
      submittingRef.current = false
      setRefreshingFlightKey(undefined)
    }
  }, [
    chatId,
    flightUnavailableMessage,
    isFlightCartMutationPending,
    isLoggedIn,
    isStreaming,
    openDrawer,
    persistFlightCartSelection,
    resolveSavedFlightSelection,
    showModal,
  ])

  const handleActiveFlightRefresh = useCallback(async(item: CartPanelFlightItem): Promise<boolean> => {
    if (!chatId || !item.flightCartItem || submittingRef.current || isStreaming || isFlightCartMutationPending) return false
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return false
    }
    const flightCheckoutItem = item.flightCartItem.item
    if (!isFlightItem(flightCheckoutItem)) return false

    submittingRef.current = true
    setFlightCartMutationPending(true)
    setRefreshingFlightKey(item.key)
    try {
      const firstFlight = flightCheckoutItem.flights?.[0]
      const isCharterFlight = firstFlight?.provider === CHARTER
      const result = await refreshMultiCartFlightItem({
        multiCartItem: item.flightCartItem,
        source: isCharterFlight && item.flightCartItem.isBundlePriced ? CHARTER : undefined,
        currentCurrency,
        currentRegionCode,
        enableSabreContent,
        useCharterDateMatching: isCharterFlight,
        email,
        requestLuxPlusPricing: !isCharterFlight && canRequestLuxPlusFlightPricing,
      })
      const checkoutItem = result.updatedMultiCartItem.item
      if (!isFlightItem(checkoutItem)) {
        throw new Error('Expected refreshed MultiCart item to contain a checkout flight item')
      }

      await upsertTripChatFlightCartItem(chatId, item.tripItemId, {
        intent: 'refresh',
        reactivationMode: resolveFlightCartReactivationMode({
          cartExcluded: item.cartExcluded,
          isAvailabilityBlocked: item.isAvailabilityBlocked,
        }),
        flightPayload: mapCheckoutFlightItemToTripChatFlightCartPayload(checkoutItem),
        returnItemId: item.returnTripItemId,
      })
      setFlightRefreshFailed(setRefreshFailedFlightKeys, item, false)
      refetchCurrentChatItinerary()
      await invalidateTripPlannerCartItems()
      return true
    } catch(error) {
      const isAssistantTurnError = isFlightCartErrorResponse(error) && error.code === 'ASSISTANT_TURN_ACTIVE'
      if (!isAssistantTurnError) {
        setFlightRefreshFailed(setRefreshFailedFlightKeys, item, true)
      }
      const message = isAssistantTurnError
        ? assistantTurnErrorMessage
        : defaultErrorMessage
      showSnackbar(message, 'critical')
      refetchCurrentChatItinerary()
      await invalidateTripPlannerCartItems()
      return false
    } finally {
      submittingRef.current = false
      setFlightCartMutationPending(false)
      setRefreshingFlightKey(undefined)
    }
  }, [
    assistantTurnErrorMessage,
    canRequestLuxPlusFlightPricing,
    chatId,
    currentCurrency,
    currentRegionCode,
    defaultErrorMessage,
    email,
    enableSabreContent,
    invalidateTripPlannerCartItems,
    isFlightCartMutationPending,
    isLoggedIn,
    isStreaming,
    refetchCurrentChatItinerary,
    setFlightCartMutationPending,
    setRefreshFailedFlightKeys,
    showModal,
  ])

  const handleRefreshFlight = useCallback(async(item: CartPanelFlightItem): Promise<boolean> => {
    if (item.flightCartItem && item.isAvailabilityBlocked) {
      // Awaited so the sequential multi-city refresh loop in useCartPanelActions
      // doesn't advance to the next leg while this one is still mutating.
      return handleActiveFlightRefresh(item)
    }
    if (isStreaming || isFlightCartMutationPending || submittingRef.current) return false
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return false
    }

    submittingRef.current = true
    setRefreshingFlightKey(item.key)
    try {
      const selection = await resolveSavedFlightSelection(item)
      if (!selection) {
        openDrawer(item, 'refresh')
        return false
      }

      const result = await persistFlightCartSelection(item, selection, 'refresh')
      if (result === 'failed') {
        openDrawer(item, 'refresh')
      }
      return result === 'success'
    } finally {
      submittingRef.current = false
      setRefreshingFlightKey(undefined)
    }
  }, [
    handleActiveFlightRefresh,
    isFlightCartMutationPending,
    isLoggedIn,
    isStreaming,
    openDrawer,
    persistFlightCartSelection,
    resolveSavedFlightSelection,
    showModal,
  ])

  const handleChangeFlight = useCallback((item: CartPanelFlightItem) => {
    openDrawer(item, 'change')
  }, [openDrawer])

  // The drawer search runs through `useSearchFlights` without a LuxPlus request
  // mode, so its journeys (and searchId) are public-priced. Persisting a drawer
  // pick straight to checkout would charge a charge-eligible member the public
  // fare. Re-resolve the chosen journey(s) with member pricing so the persisted
  // searchId matches the member fare. A multi-city group shares one member
  // search: the first leg's search caches every segment, so later legs resolve
  // from cache. Returns the original (public) selection for non-eligible
  // customers or when no matching member fare is found - never worse than before.
  const resolveCartSelectionMemberPricing = useCallback(async(
    selection: FlightSearchDrawerCartSelection,
  ): Promise<FlightSearchDrawerCartSelection> => {
    if (!canRequestLuxPlusFlightPricing) return selection
    const departingJourneyKey = selection.departingJourney.bookingInfo?.journeyKey
    if (!departingJourneyKey) return selection
    const returningJourneyKey = selection.returningJourney?.bookingInfo?.journeyKey

    const cachedDeparting = getCachedJourney(departingJourneyKey, true)
    const cachedReturning = returningJourneyKey ? getCachedJourney(returningJourneyKey, true) : undefined
    if (cachedDeparting?.searchId && (!returningJourneyKey || cachedReturning?.searchId)) {
      return {
        ...selection,
        departingJourney: cachedDeparting.journey,
        returningJourney: cachedReturning?.journey ?? selection.returningJourney,
        searchId: cachedDeparting.searchId,
        sessionId: cachedDeparting.sessionId,
        searchExpiry: cachedDeparting.cachedAt + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000,
      }
    }

    const found = await searchAndFindJourneys({
      searchParams: selection.searchParams,
      departingJourneyKey,
      returningJourneyKey,
      currency: currentCurrency,
      region: currentRegionCode,
      email,
      enableSabreContent,
      requestLuxPlusPricing: true,
    })
    if (!found?.searchId) return selection
    return {
      ...selection,
      departingJourney: found.departingJourney,
      returningJourney: found.returningJourney ?? selection.returningJourney,
      searchId: found.searchId,
      sessionId: found.sessionId,
      searchExpiry: found.searchExpiry,
    }
  }, [canRequestLuxPlusFlightPricing, currentCurrency, currentRegionCode, email, enableSabreContent])

  const handleCartFlightSelect = useCallback(async(selection: FlightSearchDrawerCartSelection) => {
    if (!chatId || !drawerState || submittingRef.current) return false
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return false
    }

    submittingRef.current = true
    const { item, mode } = drawerState
    try {
      if (item.multiCityLegs && selection.multiCityJourneys?.length) {
        for (const leg of item.multiCityLegs) {
          // Fail closed on a missing legOrder: falling back to 0 (as the old
          // code did) would map several legs onto multiCityJourneys[0] and
          // validate every leg against the first itinerary leg.
          const selectedLegIndex = isLeFlightSavedItemData(leg.savedItemData) ? getMultiCityItineraryIndex(leg.savedItemData, selection.searchParams.itinerary) : undefined
          if (selectedLegIndex === undefined) return false
          const departingJourney = selection.multiCityJourneys[selectedLegIndex]
          if (!departingJourney) return false
          const legSelection = await resolveCartSelectionMemberPricing({
            ...selection,
            departingJourney,
            returningJourney: undefined,
          })
          const result = await persistFlightCartSelection(leg, legSelection, mode)
          if (result !== 'success') return false
        }
        return true
      }
      const memberSelection = await resolveCartSelectionMemberPricing(selection)
      const result = await persistFlightCartSelection(item, memberSelection, mode)
      return result === 'success'
    } finally {
      submittingRef.current = false
    }
  }, [
    chatId,
    drawerState,
    isLoggedIn,
    persistFlightCartSelection,
    resolveCartSelectionMemberPricing,
    showModal,
  ])

  const drawerParams = useMemo(() => {
    const item = drawerState?.item
    if (!item) return undefined
    const transport = findTransportConnectionForOption(itineraryData, item.option.itemId)
    const optionWithCartSavedData = {
      ...item.option,
      savedItemData: item.savedItemData,
    }
    const returnOptionWithCartSavedData = item.returnOption ? {
      ...item.returnOption,
      savedItemData: item.returnSavedItemData ?? item.returnOption.savedItemData,
    } : undefined
    const transportParams = getFlightSearchDrawerParamsFromTransport(
      optionWithCartSavedData,
      transport,
      returnOptionWithCartSavedData,
    )
    const parsedParams = isLeFlightSavedItemData(item.savedItemData)
      ? parseSearchParams(item.savedItemData.searchParams)
      : undefined

    return {
      originAirportCode: transportParams.originAirportCode,
      originAirportName: transportParams.originAirportName,
      destinationAirportCode: transportParams.destinationAirportCode,
      destinationAirportName: transportParams.destinationAirportName,
      departureDate: transportParams.departureDate,
      returnDate: transportParams.returnDate,
      fareType: getDrawerFareType(item),
      fareClass: transportParams.fareClass,
      itinerary: item.multiCityLegs ? parsedParams?.itinerary : undefined,
      showMultiCity: item.multiCityLegs && parsedParams?.fareType === FlightsFareTypes.MULTI_CITY,
      occupants: transportParams.occupants,
    }
  }, [drawerState, itineraryData])

  const flightSearchDrawer = drawerState ? (
    <FlightSearchDrawer
      isOpen
      onClose={closeDrawer}
      originAirportCode={drawerParams?.originAirportCode}
      originAirportName={drawerParams?.originAirportName}
      destinationAirportCode={drawerParams?.destinationAirportCode}
      destinationAirportName={drawerParams?.destinationAirportName}
      departureDate={drawerParams?.departureDate}
      returnDate={drawerParams?.returnDate}
      itinerary={drawerParams?.itinerary}
      showMultiCity={drawerParams?.showMultiCity}
      fareType={drawerParams?.fareType}
      fareClass={drawerParams?.fareClass}
      occupants={drawerParams?.occupants}
      autoSearch
      onCartFlightSelect={handleCartFlightSelect}
      isCartSubmitting={isFlightCartMutationPending}
      isStreaming={isStreaming}
    />
  ) : null

  return {
    flightSearchDrawer,
    handleChooseFlight,
    handleRefreshFlight,
    handleChangeFlight,
    refreshingFlightKey,
    isFlightCartMutationPending,
  }
}
